import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Caption1, MessageBar, MessageBarBody, Spinner, tokens } from '@fluentui/react-components';
import { useApi } from '../api/useApi';
import { PageHeader } from '../components/PageHeader';
import { GroupList } from '../features/groups/GroupList';
import { MemberRow, MemberTypeBar } from '../features/groups/GroupMembers';
import { UserPicker } from '../features/users/UserPicker';
import type { DirectoryUserDto, GroupDetailDto, GroupMemberDto, GroupSummaryDto } from '../api/types';
import '../features/groups/groups.css';

export function GroupMembersPage() {
  const api = useApi();
  const [params] = useSearchParams();
  const [selected, setSelected] = useState<GroupSummaryDto[]>([]);
  const [details, setDetails] = useState<Record<string, GroupDetailDto>>({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const selectedIds = new Set(selected.map((g) => g.id));

  function toggle(g: GroupSummaryDto) {
    setSelected((prev) => (prev.some((x) => x.id === g.id) ? prev.filter((x) => x.id !== g.id) : [...prev, g]));
  }

  async function loadDetail(id: string) {
    setLoadingIds((s) => new Set(s).add(id));
    try {
      const detail = await api.getGroup(id);
      setDetails((m) => ({ ...m, [id]: detail }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingIds((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  }

  useEffect(() => {
    selected.forEach((g) => {
      if (!details[g.id]) void loadDetail(g.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Vorauswahl per ?ids= (Deep-Link aus User-Insights). Detail wird direkt
  // mitgeladen, damit der bestehende Effekt es nicht erneut anfordert.
  useEffect(() => {
    const ids = (params.get('ids') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length === 0) return;
    let active = true;
    void Promise.all(ids.map((id) => api.getGroup(id).then((detail) => detail, () => null))).then((results) => {
      if (!active) return;
      const ok = results.filter((d): d is GroupDetailDto => d !== null);
      if (ok.length === 0) return;
      setDetails((m) => {
        const next = { ...m };
        for (const d of ok) next[d.id] = d;
        return next;
      });
      setSelected(
        ok.map((d) => ({
          id: d.id,
          displayName: d.displayName,
          description: d.description,
          groupType: d.groupType,
          memberCount: d.memberCount,
          userCount: 0,
          groupCount: 0,
          deviceCount: 0,
          appCount: 0,
        })),
      );
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function addMember(user: DirectoryUserDto | undefined) {
    if (!user || selected.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await Promise.all(selected.map((g) => api.addGroupMember(g.id, user.id, user.displayName, g.displayName)));
      await Promise.all(selected.map((g) => loadDetail(g.id)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(group: GroupDetailDto, member: GroupMemberDto) {
    setBusy(true);
    setError(null);
    try {
      await api.removeGroupMember(group.id, member.id, member.displayName, group.displayName);
      await loadDetail(group.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Gruppen-Mitglieder"
        description="Gruppen links wählen — Mitglieder anzeigen, hinzufügen und entfernen."
      />

      {error ? (
        <div style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        </div>
      ) : null}

      <div className="grp-layout">
        <GroupList selected={selectedIds} onToggle={toggle} />

        <div className="grp-detail-stack">
          {selected.length === 0 ? (
            <div className="grp-panel">
              <div className="grp-empty">Wähle links eine oder mehrere Gruppen, um Mitglieder zu verwalten.</div>
            </div>
          ) : (
            <>
              <div className="grp-panel">
                <span className="grp-panel-title">Mitglied hinzufügen</span>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  {selected.length === 1
                    ? `Zur Gruppe „${selected[0].displayName}".`
                    : `Zu ${selected.length} gewählten Gruppen gleichzeitig.`}
                </Caption1>
                <UserPicker onSelect={addMember} placeholder="Person suchen und hinzufügen…" clearOnSelect />
                {busy ? <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Wird übernommen…</Caption1> : null}
              </div>

              {selected.map((g) => {
                const detail = details[g.id];
                return (
                  <div className="grp-panel" key={g.id}>
                    <div className="grp-panel-head">
                      <span className="grp-panel-title">{g.displayName}</span>
                      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                        {detail ? `${detail.memberCount} Mitglieder` : ''}
                      </Caption1>
                    </div>

                    {detail ? (
                      <MemberTypeBar members={detail.members} transitiveCount={detail.transitiveMemberCount} />
                    ) : null}

                    {loadingIds.has(g.id) && !detail ? (
                      <Spinner size="tiny" label="Lade Mitglieder…" />
                    ) : detail && detail.members.length === 0 ? (
                      <div className="grp-empty">Keine Mitglieder.</div>
                    ) : detail ? (
                      detail.members.map((m, i) => (
                        <div key={m.id}>
                          {i > 0 ? <div className="grp-member-divider" /> : null}
                          <MemberRow member={m} removeDisabled={busy} onRemove={() => removeMember(detail, m)} />
                        </div>
                      ))
                    ) : null}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
